/**
 * DissolveGroupDialog — confirmation dialog for dissolving a buddy group.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/contexts/ToastContext';
import { useDissolveBuddyGroup } from '../api';
import type { BuddyGroupSummary } from '../types/buddy-group';

interface DissolveGroupDialogProps {
  group: BuddyGroupSummary;
  open: boolean;
  onClose: () => void;
  onDissolved: () => void;
}

export function DissolveGroupDialog({ group, open, onClose, onDissolved }: DissolveGroupDialogProps) {
  const { showSuccess, showError } = useToast();
  const dissolve = useDissolveBuddyGroup();

  const handleConfirm = async () => {
    try {
      await dissolve.mutateAsync(group.id);
      showSuccess('Group dissolved', `"${group.group_name || 'Unnamed group'}" has been dissolved.`);
      onDissolved();
    } catch (err: any) {
      showError('Failed to dissolve', err?.message ?? 'An error occurred.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dissolve Buddy Group</DialogTitle>
          <DialogDescription>
            This will remove all members from <strong>"{group.group_name || 'Unnamed group'}"</strong> and unlink all associated registrations. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={dissolve.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={dissolve.isPending}>
            {dissolve.isPending ? 'Dissolving...' : 'Dissolve Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
